import React from 'react';
import { Formik, Form } from 'formik';

import { FormElement, Label } from '@dashboard/components/Form';

import { Checkbox, TextInput } from '@dashboard/components/Formik';

import { isEmpty } from 'lodash';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import FormError from '@dashboard/components/Formik/components/FormError';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import isPayingSeller from '@repo/checkout/lib/isPayingSeller';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import Features from '@dashboard/enums/features';
import usePage from '@dashboard/hooks/usePage';
import { hasRecurringPrice } from '@dashboard/services/getBillingModel';
import { UnsavedChangesReporter } from '@dashboard/components/UnsavedChanges';

const NotificationsForm = () => {
  const { page, updatePage } = usePage();
  const { seller } = useAuth();

  return (
    <Formik
      initialValues={{
        sendPaymentNotification: page?.sendPaymentNotification,
        notifyEmail: page?.notifyEmail,
        sendCanceledSubscriptionNotifications: page?.sendCanceledSubscriptionNotifications,
        sendRecurringPaymentFailedNotifications: page?.sendRecurringPaymentFailedNotifications,
        sendRecurringPaymentSucceededNotifications:
          page?.sendRecurringPaymentSucceededNotifications,
      }}
      onSubmit={async (values, actions) => {
        actions.setStatus(undefined);

        const { data, error } = await updatePage.mutateAsync(values);

        if (data) {
          actions.setStatus('');
          actions.resetForm({
            values: {
              sendPaymentNotification: data.sendPaymentNotification,
              notifyEmail: data.notifyEmail,
              sendCanceledSubscriptionNotifications: data.sendCanceledSubscriptionNotifications,
              sendRecurringPaymentFailedNotifications: data.sendRecurringPaymentFailedNotifications,
              sendRecurringPaymentSucceededNotifications:
                data.sendRecurringPaymentSucceededNotifications,
            },
          });
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ status, dirty, handleSubmit, values }) => (
        <Form onSubmit={handleSubmit}>
          <UnsavedChangesReporter />
          <FormElement>
            <Checkbox.Group>
              <Checkbox.Wrapper>
                <Checkbox id="sendPaymentNotification" name="sendPaymentNotification" />
                <Checkbox.Label htmlFor="sendPaymentNotification">
                  Enable admin notifications
                </Checkbox.Label>
              </Checkbox.Wrapper>

              <FormError name="sendPaymentNotification" />

              <>
                {hasRecurringPrice(page.product) && (
                  <>
                    <Checkbox.Wrapper>
                      <Checkbox
                        id="sendCanceledSubscriptionNotifications"
                        name="sendCanceledSubscriptionNotifications"
                      />
                      <Checkbox.Label htmlFor="sendCanceledSubscriptionNotifications">
                        Canceled subscriptions
                      </Checkbox.Label>
                    </Checkbox.Wrapper>

                    <FormError name="sendCanceledSubscriptionNotifications" />

                    <Checkbox.Wrapper>
                      <Checkbox
                        id="sendRecurringPaymentSucceededNotifications"
                        name="sendRecurringPaymentSucceededNotifications"
                      />
                      <Checkbox.Label htmlFor="sendRecurringPaymentSucceededNotifications">
                        Successful recurring payments
                      </Checkbox.Label>
                    </Checkbox.Wrapper>

                    <FormError name="sendRecurringPaymentSucceededNotifications" />

                    <Checkbox.Wrapper>
                      <Checkbox
                        id="sendRecurringPaymentFailedNotifications"
                        name="sendRecurringPaymentFailedNotifications"
                      />
                      <Checkbox.Label htmlFor="sendRecurringPaymentFailedNotifications">
                        Failed recurring payments
                      </Checkbox.Label>
                    </Checkbox.Wrapper>

                    <FormError name="sendRecurringPaymentFailedNotifications" />
                  </>
                )}
              </>
            </Checkbox.Group>
          </FormElement>

          {values.sendPaymentNotification && (
            <FormElement>
              <CanUseFeature
                feature={Features.NOTIFY_EMAIL}
                isDisabled={!isPayingSeller(seller)}
                description={
                  seller?.subscriptionStatus === 'trialing'
                    ? 'Extra notification recipients are not available during your trial. Upgrade to a paid plan to add them.'
                    : undefined
                }
              >
                <Label htmlFor="notifyEmail">Also send admin notifications to</Label>

                <TextInput
                  id="notifyEmail"
                  name="notifyEmail"
                  type="text"
                  placeholder="johndoe@gmail.com, janedoe@gmail.com"
                  border
                />
                <div className="mt-1 block text-xs font-normal text-gray-500 dark:text-muted-foreground">
                  Separate multiple email addresses with commas.
                </div>
                <FormError name="notifyEmail" />
              </CanUseFeature>
            </FormElement>
          )}

          {status && <FormElement>{status}</FormElement>}

          <FormSubmit>
            <FormSubmit.Button>Save</FormSubmit.Button>
            <FormSubmit.State visible={!!dirty} />
          </FormSubmit>
        </Form>
      )}
    </Formik>
  );
};

export default NotificationsForm;
